import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Flex,
  Heading,
  Text,
  IconButton,
  useColorModeValue,
  Badge,
  Tooltip,
  useMediaQuery,
  Link as ChakraLink
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { FaInfoCircle } from 'react-icons/fa';
import VideoCard from './VideoCard';
import { useSelector } from "react-redux";
import BurrowSpinner from '../../UI/BurrowSpinner';
import { Link as RouterLink } from 'react-router-dom';
import ThumbnailPreloader from '../../../utils/ThumbnailPreloader';

// Define pulse animation keyframe
const pulseAnimation = keyframes`
  0% { transform: scale(1); opacity: 0.7; }
  50% { transform: scale(1.1); opacity: 1; }
  100% { transform: scale(1); opacity: 0.7; }
`;

export const CardCarousel = ({ 
  data = [], 
  title = "Untitled", 
  minititle = "", 
  showDuration = false,
  columns = { base: 1, sm: 2, md: 3, lg: 4 },
  iconName = null, // Add icon name parameter
  titleColor = null // Add title color parameter
}) => {
  // Add debug logging
  console.log(`CardCarousel rendering: ${title}`, { itemCount: data?.length || 0 });

  const safeTitle = title || "untitled";
  const carouselId = `carousel-${safeTitle.replace(/\s+/g, '-').toLowerCase()}`;
  const [isMobile] = useMediaQuery("(max-width: 768px)");

  const isLoading = useSelector((state) => state.Mode?.loading) || false;
  const [showControls, setShowControls] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [safeData, setSafeData] = useState([]);
  const [isLoadingState, setIsLoading] = useState(true);
  const [isHoveringLeft, setIsHoveringLeft] = useState(false);
  const [isHoveringRight, setIsHoveringRight] = useState(false);
  const [mouseYPosition, setMouseYPosition] = useState(0);
  const [scrollVelocity, setScrollVelocity] = useState(0);
  const [isInfoActive, setIsInfoActive] = useState(false);
  const lastFrameTimeRef = useRef(performance.now());
  const animationFrameRef = useRef(null);
  const carouselRef = useRef(null);
  const autoScrollIntervalRef = useRef(null);
  const buttonHeight = 225; // Height of the button for calculations

  // --- Drag-to-scroll state ---
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragScrollLeft = useRef(0);
  const hasDragged = useRef(false);
  
  const emptyBgColor = useColorModeValue("gray.100", "gray.700");
  const emptyBorderColor = useColorModeValue("gray.200", "gray.600");
  
  // Normalize data
  useEffect(() => {
    setIsLoading(true);
    if (Array.isArray(data)) {
      // Deduplicate by video id — manifest data can contain duplicates
      const seen = new Set();
      const unique = data.filter(Boolean).filter(video => {
        const id = video?.id;
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      });
      setSafeData(unique);
    } else {
      setSafeData([]);
    }
    setIsLoading(false);
  }, [data]);
  
  // Preload thumbnails for upcoming videos to improve perceived performance
  useEffect(() => {
    if (safeData.length > 0) {
      // Defer preloading to idle time to avoid blocking main thread
      import('../../../utils/scheduleIdle').then(({ scheduleIdle }) => {
        scheduleIdle(() => {
          ThumbnailPreloader.preloadVideos(safeData, 0, 10);
        }, { timeout: 2000 });
      });
    }
  }, [safeData]);
  
  // --- Drag-to-scroll handlers ---
  const onMouseDown = useCallback((e) => {
    if (!carouselRef.current) return;
    e.preventDefault(); // Prevent native image/link drag
    isDragging.current = true;
    hasDragged.current = false;
    dragStartX.current = e.pageX - carouselRef.current.offsetLeft;
    dragScrollLeft.current = carouselRef.current.scrollLeft;
    carouselRef.current.style.cursor = 'grabbing';
    carouselRef.current.style.userSelect = 'none';
  }, []);

  // Block native drag on images/links inside the carousel
  const onDragStart = useCallback((e) => {
    e.preventDefault();
  }, []);

  const onMouseMove = useCallback((e) => {
    if (!isDragging.current || !carouselRef.current) return;
    e.preventDefault();
    const x = e.pageX - carouselRef.current.offsetLeft;
    const walk = (x - dragStartX.current) * 1.5;
    if (Math.abs(walk) > 5) hasDragged.current = true;
    carouselRef.current.scrollLeft = dragScrollLeft.current - walk;
    setScrollPosition(carouselRef.current.scrollLeft);
  }, []);

  const onMouseUp = useCallback(() => {
    if (!carouselRef.current) return;
    isDragging.current = false;
    carouselRef.current.style.cursor = 'grab';
    carouselRef.current.style.userSelect = '';
    if (hasDragged.current) {
      const preventClick = (e) => {
        e.stopPropagation();
        e.preventDefault();
      };
      carouselRef.current.addEventListener('click', preventClick, { capture: true, once: true });
    }
  }, []);

  const onMouseLeave_drag = useCallback(() => {
    if (isDragging.current) onMouseUp();
  }, [onMouseUp]);

  // More efficient scrolling using requestAnimationFrame instead of setInterval
  // This provides smoother scrolling with less lag
  const updateScroll = useCallback((timestamp) => {
    if (!carouselRef.current) return;
    
    // Calculate time since last frame for consistent speed regardless of frame rate
    const deltaTime = timestamp - lastFrameTimeRef.current;
    lastFrameTimeRef.current = timestamp;
    
    // Apply scroll with the current velocity
    if (scrollVelocity !== 0) {
      carouselRef.current.scrollLeft += scrollVelocity * (deltaTime / 16); // Normalize to ~60fps
      setScrollPosition(carouselRef.current.scrollLeft);
    }
    
    animationFrameRef.current = requestAnimationFrame(updateScroll);
  }, [scrollVelocity]);
  
  // Start/stop the animation frame loop based on hover state
  useEffect(() => {
    if (isHoveringLeft || isHoveringRight) {
      // Calculate speed factor based on vertical position (0-1 range)
      // Top of button = faster, bottom = slower
      const position = 1 - (mouseYPosition / buttonHeight);
      const speedFactor = Math.max(0.5, position * 4); // Scale between 0.5x and 4x speed
      
      // Base speed is 3px, adjusted by position
      const baseSpeed = 3;
      const adjustedSpeed = baseSpeed * speedFactor;
      
      // Set the scroll velocity based on direction
      setScrollVelocity(isHoveringLeft ? -adjustedSpeed : adjustedSpeed);
      
      // Start the animation loop if not already running
      if (!animationFrameRef.current) {
        lastFrameTimeRef.current = performance.now();
        animationFrameRef.current = requestAnimationFrame(updateScroll);
      }
    } else {
      // Stop scrolling when not hovering
      setScrollVelocity(0);
      
      // Cancel animation frame
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    }
    
    // Cleanup on component unmount
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [isHoveringLeft, isHoveringRight, mouseYPosition, updateScroll]);
  
  // Calculate gradient opacity and button transparency based on mouse position
  const getButtonStyles = useMemo(() => {
    // Normalize position to 0-1 range (1 at top, 0 at bottom)
    const normalizedPosition = 1 - (mouseYPosition / buttonHeight);
    
    // Calculate gradient opacity (100% at top, 10% at bottom)
    const gradientOpacity = 0.1 + (normalizedPosition * 0.9);
    
    // Calculate button opacity (100% at top, 40% at bottom)
    const buttonOpacity = 0.4 + (normalizedPosition * 0.6);
    
    return {
      gradient: {
        background: `linear-gradient(to bottom, rgba(150,150,150,${gradientOpacity}), rgba(124, 121, 121, 0))`,
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: 'none',
        transition: 'background 0.4s ease-in-out', // Slowed down and eased
        borderRadius: '12px',
      },
      button: {
        opacity: buttonOpacity,
        transition: 'opacity 0.4s ease-in-out' // Slowed down and eased
      }
    };
  }, [mouseYPosition]);
  
  // Get the arrow color based on color mode
  const arrowColor = useColorModeValue("gray.800", "white.200");
  
  // Function to track mouse vertical position - debounced for better performance
  const handleMouseMove = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const relativeY = e.clientY - rect.top;
    setMouseYPosition(Math.min(Math.max(0, relativeY), buttonHeight));
  }, []);
  
  // Handle scrolling
  const scrollRow = (direction) => {
    if (!carouselRef.current) return;
    
    const carousel = carouselRef.current;
    // Mobile: scroll one card (220px + 4px gap), Desktop: scroll 3 cards (299px + 16px gap)
    const cardWidth = isMobile ? (220 + 4) : (299 + 16);
    const cardsToScroll = isMobile ? 1 : 3;
    const scrollAmount = direction === 'left' ? -cardWidth * cardsToScroll : cardWidth * cardsToScroll;
    
    carousel.scrollBy({ 
      left: scrollAmount,
      behavior: 'smooth'
    });
  };
  
  // Update scroll position for button states
  const handleScroll = () => {
    if (!carouselRef.current) return;
    
    const carousel = carouselRef.current;
    setScrollPosition(carousel.scrollLeft);
  };
  
  // Add scroll event listener
  useEffect(() => {
    const carousel = carouselRef.current;
    if (carousel) {
      carousel.addEventListener('scroll', handleScroll);
      return () => carousel.removeEventListener('scroll', handleScroll);
    }
  }, []);
  
  // Calculate if we can scroll right
  const canScrollRight = () => {
    if (!carouselRef.current) return false;
    
    const carousel = carouselRef.current;
    return carousel.scrollWidth > carousel.clientWidth && 
           scrollPosition < carousel.scrollWidth - carousel.clientWidth - 10;
  };

  // Don't render empty categories
  if (safeData.length === 0) {
    return null;
  }

  return (
    <Box mb={{ base: 3, sm: 4, md: 6, lg: 8 }}>
      {/* Display the large heading title with info toggle button */}
      <Flex align="center" ml={4} mb={{ base: 2, md: 4 }}>
        <ChakraLink
          as={RouterLink}
          to={`/categories/${encodeURIComponent(safeTitle.toLowerCase())}`}
          _hover={{ textDecoration: 'none' }}
          aria-label={`View all ${safeTitle} videos`}
          display="flex"
          alignItems="center"
        >
          <Flex 
            alignItems="center"
            transition="all 0.2s"
            _hover={{ 
              transform: 'translateX(2px)', 
              opacity: 0.8 
            }}
          >
            {/* Display category icon if provided */}
            {iconName && (
              <Box 
                mr={2} 
                display="inline-flex" 
                alignItems="center" 
                color={titleColor || "inherit"}
                transition="color 0.2s ease-in-out"
              >
                {/* Dynamically render the icon based on the iconName string */}
                {(() => {
                  // Import all possible icons that might be used
                  const { 
                    FaFilm, FaFlask, FaGhost, FaHistory, FaMusic, FaHeart, 
                    FaHeartBroken, FaLaugh, FaUserAstronaut, FaMedal, 
                    FaNewspaper, FaPaw, FaTrophy, FaMedkit, FaBroadcastTower,
                    FaDraftingCompass, FaUserMd, FaUniversity, FaEye, 
                    FaSyringe, FaBalanceScale, FaDna, FaMagnet, FaPlane, 
                    FaHourglassHalf, FaBinoculars, FaBolt 
                  } = require('react-icons/fa');
                  
                  const { 
                    GiMicrophone, GiUfo, GiEarthAmerica, GiWhiteBook, 
                    GiAlienStare, GiRobotGolem, GiCrystalBall, GiMoai, 
                    GiOni, GiTreeBranch 
                  } = require('react-icons/gi');
                  
                  const { IoMdPlanet } = require('react-icons/io');
                  const { BsStars } = require('react-icons/bs');
                  const { WiTornado } = require('react-icons/wi');
                  const { PiSpiralBold } = require('react-icons/pi');
                  
                  // Map of icon name strings to actual components
                  const iconMap = {
                    FaFilm, FaFlask, FaGhost, FaHistory, FaMusic, FaHeart, 
                    FaHeartBroken, FaLaugh, FaUserAstronaut, FaMedal, 
                    FaNewspaper, FaPaw, FaTrophy, FaMedkit, FaBroadcastTower,
                    FaDraftingCompass, FaUserMd, FaUniversity, FaEye, 
                    FaSyringe, FaBalanceScale, FaDna, FaMagnet, FaPlane, 
                    FaHourglassHalf, FaBinoculars, FaBolt,
                    GiMicrophone, GiUfo, GiEarthAmerica, GiWhiteBook, 
                    GiAlienStare, GiRobotGolem, GiCrystalBall, GiMoai, 
                    GiOni, GiTreeBranch, IoMdPlanet, BsStars, WiTornado, 
                    PiSpiralBold
                  };
                  
                  // Return the icon component if it exists
                  const IconComponent = iconMap[iconName];
                  
                  return IconComponent ? (
                    <IconComponent size="1.4em" />
                  ) : (
                    <BsStars size="1.4em" />
                  );
                })()}
              </Box>
            )}
            <Heading 
              size={{ base: "md", md: "lg" }} 
              textAlign="left"
              color={titleColor || "inherit"}
              transition="color 0.2s ease-in-out"
            >
              {safeTitle}
            </Heading>
            <ChevronRightIcon ml={1} w={5} h={5} color="gray.500" />
          </Flex>
        </ChakraLink>
        <Tooltip label="Toggle info display for this row" fontSize="sm">
          <IconButton
            aria-label="Toggle information"
            icon={<FaInfoCircle />}
            size="sm"
            variant="ghost"
            ml={2}
            color={isInfoActive ? "blue.500" : "gray.400"}
            onClick={() => setIsInfoActive(!isInfoActive)}
            _hover={{ color: "blue.600" }}
            sx={{
              '@media (max-width: 768px)': {
                animation: !isInfoActive ? `${pulseAnimation} 2s infinite ease-in-out` : 'none',
                boxShadow: isInfoActive ? '0 0 8px #4299E1' : 'none',
                transition: 'all 0.3s ease'
              }
            }}
          />
        </Tooltip>
      </Flex>
      
      {minititle && (
        <Text fontSize="sm" color="gray.500" mb={{ base: 2, md: 4 }} textAlign="left" ml={4}>
          {minititle}
        </Text>
      )}

      {/* Use carouselId instead of direct string interpolation */}
      <Box 
        id={carouselId}
        position="relative"
        onMouseEnter={() => setShowControls(true)}
        onMouseLeave={() => setShowControls(false)}
        bg="transparent"
      >
        <Box mb={1} display="flex" alignItems="center" px={4} flexWrap="nowrap" overflow="hidden">
          {/* Removed duplicate heading here */}
          {minititle && (
            <Text 
              fontSize="sm" 
              color="gray.500" 
              ml={1} 
              flexShrink={1}
              whiteSpace="nowrap"
              overflow="hidden"
              textOverflow="ellipsis"
            >
              {minititle}
            </Text>
          )}

        </Box>

        {isLoadingState ? (
          <Box textAlign="center" py={10}>
            <BurrowSpinner size="xl" color="blue.500" />
            <Text mt={4}>Loading videos...</Text>
          </Box>
        ) : safeData.length === 0 ? (
          <Box
            p={5}
            borderWidth="1px"
            borderStyle="dashed"
            borderRadius="md"
            borderColor={emptyBorderColor}
            bg={emptyBgColor}
            textAlign="center"
            mx={4}
          >
            <Text color="gray.500">No videos in this category</Text>
          </Box>
        ) : (
          <Box position="relative" display="flex" alignItems="center">
            {/* Left scroll button */}
            {isMobile ? (
              /* Mobile: full-height gradient strip matching FeaturedCarousel style */
              <Box
                position="absolute"
                left={0}
                top={0}
                bottom={0}
                w="44px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                bg="linear-gradient(to right, rgba(0,0,0,0.6), transparent)"
                borderRadius="12px 0 0 12px"
                opacity={scrollPosition <= 0 ? 0 : 1}
                pointerEvents={scrollPosition <= 0 ? 'none' : 'auto'}
                transition="opacity 0.3s"
                zIndex={20}
                cursor="pointer"
                onClick={(e) => { e.stopPropagation(); scrollRow('left'); }}
              >
                <ChevronLeftIcon boxSize={8} color="white" />
              </Box>
            ) : (
              /* Desktop: hover-activated with gradient based on mouse position */
              <Box
                position="relative"
                height="225px"
                width="40px"
                minWidth="40px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                onMouseEnter={() => setIsHoveringLeft(true)}
                onMouseLeave={() => setIsHoveringLeft(false)}
                onMouseMove={handleMouseMove}
                cursor={scrollPosition <= 0 ? "default" : "pointer"}
                opacity={showControls || isHoveringLeft ? 1 : 0}
                transition="opacity 0.8s ease-in-out"
                borderRadius="12px 0 0 12px"
                overflow="hidden"
                zIndex={20}
              >
                {isHoveringLeft && <Box style={getButtonStyles.gradient} />}
                <IconButton
                  aria-label="Scroll left"
                  icon={<ChevronLeftIcon boxSize={8} color={arrowColor} />}
                  height="225px"
                  width="40px"
                  onClick={() => scrollRow('left')}
                  variant="unstyled"
                  bg="transparent"
                  _hover={{ bg: "transparent" }}
                  isDisabled={scrollPosition <= 0}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  borderRadius="12px 0 0 12px"
                  zIndex="1"
                  style={isHoveringLeft ? getButtonStyles.button : undefined}
                />
              </Box>
            )}
            
            {/* Content row - auto-scrollable + drag-to-scroll */}
            <Flex
              id={`carousel-${title.replace(/\s+/g, '-')}`}
              ref={carouselRef}
              overflowX="auto"
              overflowY="hidden"
              cursor="grab"
              onMouseDown={onMouseDown}
              onMouseMove={onMouseMove}
              onMouseUp={onMouseUp}
              onMouseLeave={onMouseLeave_drag}
              onDragStart={onDragStart}
              css={{
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': {
                  display: 'none',
                },
              }}
              gap={{ base: 1, md: 4 }} /* Responsive gap between cards, further reduced for mobile */
              flex="1"
              py={{ base: 1, md: 2 }} /* Responsive vertical padding */
              bg={showControls ? "rgba(245, 245, 247, 0.5)" : "transparent"}
              transition="background-color 0.3s"
            >
              {safeData.map((video, index) => (
                <React.Fragment key={video?.id || index}>
                  {/* Mobile VideoCard */}
                  <Box 
                    minWidth="220px"
                    maxWidth="220px"
                    p={{ base: 0.5, sm: 1 }}
                    display={{ base: "block", md: "none" }}
                  >
                    <VideoCard 
                      video={video} 
                      width="220px"
                      height="124px"
                      titleHeight="18px"
                      fontSize="12px"
                      isRowHovered={showControls || isInfoActive}
                      forceHover={isInfoActive}
                    />
                  </Box>
                  
                  {/* Desktop VideoCard */}
                  <Box 
                    minWidth="299px"
                    maxWidth="299px"
                    p={1}
                    display={{ base: "none", md: "block" }}
                  >
                    <VideoCard 
                      video={video} 
                      width="299px"
                      height="168px"
                      titleHeight="24px"
                      fontSize="16px"
                      isRowHovered={showControls || isInfoActive}
                      forceHover={isInfoActive}
                    />
                  </Box>
                </React.Fragment>
              ))}
            </Flex>
            
            {/* Right scroll button */}
            {isMobile ? (
              /* Mobile: full-height gradient strip matching FeaturedCarousel style */
              <Box
                position="absolute"
                right={0}
                top={0}
                bottom={0}
                w="44px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                bg="linear-gradient(to left, rgba(0,0,0,0.6), transparent)"
                borderRadius="0 12px 12px 0"
                opacity={!canScrollRight() ? 0 : 1}
                pointerEvents={!canScrollRight() ? 'none' : 'auto'}
                transition="opacity 0.3s"
                zIndex={20}
                cursor="pointer"
                onClick={(e) => { e.stopPropagation(); scrollRow('right'); }}
              >
                <ChevronRightIcon boxSize={8} color="white" />
              </Box>
            ) : (
              /* Desktop: hover-activated with gradient based on mouse position */
              <Box
                position="relative"
                height="225px"
                width="40px"
                minWidth="40px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                onMouseEnter={() => setIsHoveringRight(true)}
                onMouseLeave={() => setIsHoveringRight(false)}
                onMouseMove={handleMouseMove}
                cursor={!canScrollRight() ? "default" : "pointer"}
                opacity={showControls || isHoveringRight ? 1 : 0}
                transition="opacity 0.8s ease-in-out"
                borderRadius="0 12px 12px 0"
                overflow="hidden"
                zIndex={20}
              >
                {isHoveringRight && <Box style={getButtonStyles.gradient} />}
                <IconButton
                  aria-label="Scroll right"
                  icon={<ChevronRightIcon boxSize={8} color={arrowColor} />}
                  height="225px"
                  width="40px"
                  onClick={() => scrollRow('right')}
                  variant="unstyled"
                  bg="transparent"
                  _hover={{ bg: "transparent" }}
                  isDisabled={!canScrollRight()}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  borderRadius="0 12px 12px 0"
                  zIndex="1"
                  style={isHoveringRight ? getButtonStyles.button : undefined}
                />
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};
